import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/services/api-client';
import type { Game, ApiError } from '@/services/api-client';

interface UseGameDataReturn {
  game: Game | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch game data by ID
 * @param gameId - The ID of the game to fetch
 * @returns Game data, loading state, error state, and refetch function
 */
export function useGameData(gameId: string | null): UseGameDataReturn {
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchGame = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!gameId) {
      setGame(null);
      setError(null);
      setLoading(false); // Fix: Reset loading state when gameId is null
      return;
    }

    setLoading(true);
    setError(null);

    // Create a new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const gameData = await apiClient.getGame(gameId, { signal: abortController.signal });
      
      // Only update state if the request wasn't aborted
      if (!abortController.signal.aborted) {
        setGame(gameData);
        setLoading(false);
      }
    } catch (err: any) {
      // Only update error state if the request wasn't aborted
      if (!abortController.signal.aborted) {
        // Check if it's an abort error
        if (err.name === 'AbortError') {
          // Request was aborted, don't update error state
          return;
        }
        const apiError = err as ApiError;
        setError(new Error(apiError.message || 'Failed to fetch game data'));
        setGame(null);
        setLoading(false);
      }
    }
  }, [gameId]);

  useEffect(() => {
    fetchGame();
    
    // Cleanup function to abort request on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchGame]);

  return {
    game,
    loading,
    error,
    refetch: fetchGame,
  };
}